import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, Users, Calendar, BookOpen, ShoppingBag, Clock,
  ChevronRight, FileText, ArrowLeft, Eye, EyeOff
} from 'lucide-react';

// Parent Preview Mode - Shows admin what parents see with placeholder data
const ParentPreviewPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPreviewBanner, setShowPreviewBanner] = useState(true);

  // Placeholder data for preview
  const mockUser = { full_name: 'Sarah Johnson', email: 'sarah.johnson@example.com', phone: '07700 900123' };
  
  const mockChildren = [
    { id: '1', name: 'Emily', surname: 'Johnson', age: 7, year_group: 2, medical_conditions: 'None', school_class: '2B' },
    { id: '2', name: 'Oliver', surname: 'Johnson', age: 9, year_group: 4, medical_conditions: 'Mild nut allergy', school_class: '4A' },
  ];

  const mockEnrollments = [
    { id: '1', child_name: 'Emily Johnson', class_name: 'Art Adventures - Tuesday 3:30pm', status: 'confirmed', amount: 96 },
    { id: '2', child_name: 'Oliver Johnson', class_name: 'Art Adventures - Thursday 3:30pm', status: 'confirmed', amount: 89 },
  ];

  const mockSummaries = [
    { id: '1', week_number: 10, class_name: 'Art Adventures', content: 'This week we explored watercolour techniques! The children created beautiful sunset paintings using wet-on-wet methods. Emily showed great creativity with her colour mixing!', date: '2026-09-15' },
    { id: '2', week_number: 9, class_name: 'Art Adventures', content: 'We started our autumn project this week, collecting leaves and creating nature collages. Oliver impressed everyone with his detailed leaf arrangements.', date: '2026-09-08' },
  ];

  const navItems = [
    { id: 'dashboard', icon: <Home className="w-5 h-5" />, label: 'Dashboard' },
    { id: 'children', icon: <Users className="w-5 h-5" />, label: 'Children' },
    { id: 'enroll', icon: <Calendar className="w-5 h-5" />, label: 'Enroll' },
    { id: 'summaries', icon: <BookOpen className="w-5 h-5" />, label: 'Summaries' },
    { id: 'waiting-list', icon: <Clock className="w-5 h-5" />, label: 'Waiting List' },
    { id: 'parent-info', icon: <FileText className="w-5 h-5" />, label: 'Parent Info' },
  ];

  // Dashboard View
  const DashboardView = () => (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">
        Welcome, {mockUser.full_name.split(' ')[0]}!
      </h1>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">Children</p>
              <p className="font-heading text-2xl font-bold text-slate-900">{mockChildren.length}</p>
            </div>
          </div>
        </div>
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-pink-500" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">Active Enrollments</p>
              <p className="font-heading text-2xl font-bold text-slate-900">{mockEnrollments.length}</p>
            </div>
          </div>
        </div>
        <div className="card-plain">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="font-body text-slate-500 text-sm">This Term</p>
              <p className="font-heading text-2xl font-bold text-slate-900">Autumn 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <button onClick={() => setActiveTab('children')} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left">
            <span className="font-body font-semibold text-slate-700">Manage Children</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          <button onClick={() => setActiveTab('enroll')} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left">
            <span className="font-body font-semibold text-slate-700">Enroll in Class</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          <button onClick={() => setActiveTab('summaries')} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left">
            <span className="font-body font-semibold text-slate-700">Weekly Summaries</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          <button className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors text-left">
            <span className="font-body font-semibold text-slate-700">Materials Shop</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Recent Enrollments */}
      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Your Enrollments</h2>
        <div className="space-y-3">
          {mockEnrollments.map(enrollment => (
            <div key={enrollment.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-heading font-bold text-slate-900">{enrollment.child_name}</p>
                <p className="font-body text-sm text-slate-600">{enrollment.class_name}</p>
              </div>
              <div className="text-right">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-teal-100 text-teal-700">
                  {enrollment.status}
                </span>
                <p className="font-body text-sm text-slate-500 mt-1">£{enrollment.amount}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Children View
  const ChildrenView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">My Children</h1>
        <button className="btn-primary">Add Child</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {mockChildren.map(child => (
          <div key={child.id} className="card-plain">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-pink-400 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {child.name[0]}
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900">{child.name} {child.surname}</h3>
                  <p className="font-body text-slate-500">Age {child.age} • Year {child.year_group}</p>
                </div>
              </div>
              <button className="text-slate-400 hover:text-slate-600">Edit</button>
            </div>
            <div className="space-y-2 text-sm">
              <p className="font-body"><span className="text-slate-500">Class:</span> <span className="text-slate-700">{child.school_class}</span></p>
              <p className="font-body"><span className="text-slate-500">Medical:</span> <span className="text-slate-700">{child.medical_conditions}</span></p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Enroll View
  const EnrollView = () => (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Enroll in Class</h1>
      
      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Select School</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <button className="p-4 border-2 border-teal-500 bg-teal-50 rounded-xl text-left">
            <p className="font-heading font-bold text-slate-900">St John's C of E Primary</p>
            <p className="font-body text-sm text-slate-500">2 classes available</p>
          </button>
          <button className="p-4 border-2 border-slate-200 rounded-xl text-left hover:border-teal-300">
            <p className="font-heading font-bold text-slate-900">Oakwood Academy</p>
            <p className="font-body text-sm text-slate-500">1 class available</p>
          </button>
        </div>
      </div>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Available Classes</h2>
        <div className="space-y-3">
          <div className="p-4 border border-slate-200 rounded-xl hover:border-teal-300 cursor-pointer">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-heading font-bold text-slate-900">Art Adventures</p>
                <p className="font-body text-sm text-slate-500">Tuesday 3:30pm - 4:30pm • 12 weeks</p>
              </div>
              <div className="text-right">
                <p className="font-heading font-bold text-teal-600">£96</p>
                <p className="font-body text-xs text-slate-500">5 spots left</p>
              </div>
            </div>
          </div>
          <div className="p-4 border border-slate-200 rounded-xl hover:border-teal-300 cursor-pointer">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-heading font-bold text-slate-900">Art Adventures</p>
                <p className="font-body text-sm text-slate-500">Thursday 3:30pm - 4:30pm • 12 weeks</p>
              </div>
              <div className="text-right">
                <p className="font-heading font-bold text-teal-600">£96</p>
                <p className="font-body text-xs text-slate-500">3 spots left</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Summaries View
  const SummariesView = () => (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Weekly Summaries</h1>
      
      <div className="space-y-4">
        {mockSummaries.map(summary => (
          <div key={summary.id} className="card-plain">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading font-bold text-slate-900">Week {summary.week_number} - {summary.class_name}</h3>
                <p className="font-body text-sm text-slate-500">{new Date(summary.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
            <p className="font-body text-slate-700 leading-relaxed">{summary.content}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // Waiting List View
  const WaitingListView = () => (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Waiting List</h1>
      
      <div className="card-plain text-center py-12">
        <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="font-body text-slate-600">You're not on any waiting lists.</p>
        <button onClick={() => setActiveTab('enroll')} className="btn-primary mt-4">Browse Classes</button>
      </div>
    </div>
  );

  // Parent Info View
  const ParentInfoView = () => (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Parent Information</h1>
      
      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Your Details</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-slate-500 mb-1">Full Name</label>
            <p className="font-body text-slate-900">{mockUser.full_name}</p>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-500 mb-1">Email</label>
            <p className="font-body text-slate-900">{mockUser.email}</p>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-500 mb-1">Phone</label>
            <p className="font-body text-slate-900">{mockUser.phone}</p>
          </div>
        </div>
      </div>

      <div className="card-plain">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Important Documents</h2>
        <div className="space-y-2">
          <a href="#" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
            <span className="font-body text-slate-700">Terms & Conditions</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </a>
          <a href="#" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
            <span className="font-body text-slate-700">Privacy Policy</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </a>
          <a href="#" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
            <span className="font-body text-slate-700">Materials Information</span>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </a>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'children': return <ChildrenView />;
      case 'enroll': return <EnrollView />;
      case 'summaries': return <SummariesView />;
      case 'waiting-list': return <WaitingListView />;
      case 'parent-info': return <ParentInfoView />;
      default: return <DashboardView />;
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Preview Mode Banner */}
      {showPreviewBanner && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5" />
              <span className="font-body font-semibold">Preview Mode</span>
              <span className="font-body text-purple-100">— You're viewing the Parent Portal as parents see it (with placeholder data)</span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/admin')} 
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Admin
              </button>
              <button 
                onClick={() => setShowPreviewBanner(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Hide banner"
              >
                <EyeOff className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="card-plain sticky top-24">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Parent Portal</h2>
              <nav className="space-y-1">
                {navItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body font-semibold transition-colors text-left ${
                      activeTab === item.id
                        ? 'bg-teal-600 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    data-testid={`preview-nav-${item.id}`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                ))}
              </nav>

              {/* Back to Admin link at bottom of sidebar */}
              {!showPreviewBanner && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <button 
                    onClick={() => navigate('/admin')} 
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body font-semibold text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Admin
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentPreviewPage;
